import React from 'react';
import { Link } from 'react-router-dom';
import { Rss, Inbox, RefreshCw, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import PostCard, { PostCardSkeleton } from './PostCard';

const API = process.env.REACT_APP_BACKEND_URL;
const SKELETON_KEYS = ['s1', 's2', 's3', 's4', 's5', 's6'];

export function BlogHero({ eyebrow = 'Blog', title, description, crumbs = [], children }) {
  return (
    <section className="hero-bg py-14 md:py-20">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        {crumbs.length > 0 && (
          <nav aria-label="Breadcrumb" data-testid="blog-breadcrumb" className="mb-5 text-sm text-slate-500 flex flex-wrap items-center gap-1">
            {crumbs.map((c, i) => (
              <span key={c.path || c.name} className="inline-flex items-center gap-1">
                {i > 0 && <ChevronRight className="h-3.5 w-3.5" />}
                {c.path ? <Link to={c.path} className="hover:underline" style={{ color: '#2d7d9a' }}>{c.name}</Link> : <span aria-current="page" className="text-slate-700 font-medium">{c.name}</span>}
              </span>
            ))}
          </nav>
        )}
        <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>{eyebrow}</span>
        <h1 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight max-w-3xl" style={{ color: '#1a3a5c' }}>{title}</h1>
        {description && <p className="mt-5 text-lg text-slate-600 max-w-2xl">{description}</p>}
        {children}
      </div>
    </section>
  );
}

export function PostGrid({ posts, loading, error, onRetry, emptyText = 'Noch keine Beiträge – schau bald wieder vorbei.', featuredFirst = false }) {
  if (error) {
    return (
      <div role="alert" data-testid="blog-error" className="card-soft p-10 text-center">
        <p className="font-semibold" style={{ color: '#1a3a5c' }}>{error.status === 503 ? 'Der Blog ist noch nicht freigeschaltet.' : 'Beiträge konnten nicht geladen werden.'}</p>
        <p className="mt-2 text-sm text-slate-600">{error.message}</p>
        {onRetry && <Button onClick={onRetry} variant="outline" className="btn-outline-teal rounded-full mt-5 h-11 px-6"><RefreshCw className="mr-2 h-4 w-4" /> Erneut versuchen</Button>}
      </div>
    );
  }
  if (loading && !posts.length) {
    return <div data-testid="blog-loading" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" aria-busy="true">{SKELETON_KEYS.map((k) => <PostCardSkeleton key={k} />)}</div>;
  }
  if (!posts.length) {
    return (
      <div data-testid="blog-empty" className="card-soft p-14 text-center">
        <div className="h-14 w-14 mx-auto rounded-2xl flex items-center justify-center" style={{ background: '#e6f2f7' }}><Inbox className="h-7 w-7" style={{ color: '#3d94b8' }} /></div>
        <p className="mt-4 font-semibold" style={{ color: '#1a3a5c' }}>{emptyText}</p>
      </div>
    );
  }
  const [first, ...rest] = posts;
  return (
    <div className="space-y-6">
      {featuredFirst && <PostCard post={first} featured />}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {(featuredFirst ? rest : posts).map((p) => <PostCard key={p.slug} post={p} />)}
      </div>
    </div>
  );
}

export function LoadMore({ hasMore, loading, onClick }) {
  if (!hasMore) return null;
  return (
    <div className="mt-10 text-center">
      <Button data-testid="blog-load-more" onClick={onClick} disabled={loading} className="btn-teal rounded-full h-12 px-8">
        {loading ? 'Lädt …' : 'Weitere Beiträge laden'}
      </Button>
    </div>
  );
}

export function RssLink() {
  return (
    <a data-testid="blog-rss-link" href={`${API}/api/rss.xml`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: '#2d7d9a' }}>
      <Rss className="h-4 w-4" /> RSS-Feed
    </a>
  );
}
